import { zodResolver } from '@hookform/resolvers/zod';
import {
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  FormButton,
  ImageUploader,
  Input,
  SelectInput,
  string,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminRecordKeepingOrganizationsParams,
  RecordKeepingOrganizationsBillingFrequency,
  UploadAssetBodyContentType,
  useDeleteAdminRecordKeepingOrganization,
  useGetAdminRecordKeeperTypes,
  useGetAdminRecordKeepingOrganizations,
  usePostAdminRecordKeepingOrganization,
  useUploadAsset
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import { LinkButton, RkoActionsCell } from '../../components';
import { assetUrl } from '../../helpers';

type RecordKeepingOrganization = {
  city?: string;
  name: string;
  recordKeeperTypeId?: string;
  RecordKeeperTypes?: { description?: string };
  recordKeepingOrganizationId: string;
  state?: string;
};

type CreateFormProps = {
  address: string;
  billingFrequency: string;
  city: string;
  contact: string;
  email: string;
  logoUrl: string;
  name: string;
  phone: string;
  recordKeeperClientId: string;
  recordKeeperTypeId: string;
  recordKeeperUrl: string;
  state: string;
  tiaaManagedSourceId: string;
  tiaaTargetSourceId: string;
  tiaaTrustId: string;
  zip: string;
};

const createResolver = zodResolver(
  z.object({
    address: string(),
    billingFrequency: string(),
    city: string(),
    contact: string(),
    email: string(),
    logoUrl: string(),
    name: string({ maxLength: 100, required: true }),
    phone: string(),
    recordKeeperClientId: string(),
    recordKeeperTypeId: string({ required: true }),
    recordKeeperUrl: string(),
    state: string(),
    tiaaManagedSourceId: string(),
    tiaaTargetSourceId: string(),
    tiaaTrustId: string(),
    zip: string()
  })
);

const createDefaultValues = {
  address: '',
  billingFrequency: '',
  city: '',
  contact: '',
  email: '',
  logoUrl: '',
  name: '',
  phone: '',
  recordKeeperClientId: '',
  recordKeeperTypeId: '',
  recordKeeperUrl: '',
  state: '',
  tiaaManagedSourceId: '',
  tiaaTargetSourceId: '',
  tiaaTrustId: '',
  zip: ''
};

const billingFrequencyOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' }
];

const RecordKeepingOrganizationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [isCreateOpen, , setIsCreateOpen] = useToggle(false);

  const [query, setQuery] = useState<GridQuery>();

  const filters: FilterDef[] = [
    { label: 'Search', name: 'search', type: 'text' }
  ];

  const recordKeeperTypes = useGetAdminRecordKeeperTypes();
  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload logo')
    }
  });
  const createOrg = usePostAdminRecordKeepingOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to create record keeping organization'),
      onSuccess: result => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/record-keeping-organizations']
        });
        setIsCreateOpen(false);
        createMethods.reset();
        notifications.success('Record keeping organization created');
        navigate({
          to: `/recordkeepers/${result.recordKeepingOrganizationId}`
        });
      }
    }
  });
  const deleteOrg = useDeleteAdminRecordKeepingOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to delete record keeping organization'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/record-keeping-organizations']
        });
        notifications.success('Record keeping organization deleted');
      }
    }
  });

  const createMethods = useForm<CreateFormProps>({
    defaultValues: createDefaultValues,
    mode: 'all',
    resolver: createResolver
  });

  const onCreateSubmit = (data: CreateFormProps) => {
    createOrg.mutate({
      data: {
        ...data,
        billingFrequency:
          (data.billingFrequency as RecordKeepingOrganizationsBillingFrequency) ||
          undefined
      }
    });
  };

  const logoUrl = createMethods.watch('logoUrl');
  const isUploadingLogo = uploadAsset.isPending;

  const recordKeeperTypeOptions = (recordKeeperTypes.data ?? []).map(type => ({
    label: type.description,
    value: type.recordKeeperTypeId
  }));

  const columns: GridColDef<RecordKeepingOrganization>[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-component='link'
          data-testid={camelCase(params.row.name)}
          params={{
            recordKeepingOrganizationId: params.row.recordKeepingOrganizationId
          }}
          to='/recordkeepers/$recordKeepingOrganizationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'city',
      flex: 1,
      headerName: 'City'
    },
    {
      field: 'state',
      flex: 0.75,
      headerName: 'State'
    },
    {
      field: 'recordKeeperTypeId',
      flex: 1,
      headerName: 'Record Keeper Type',
      sortable: false,
      valueGetter: (_value, row) =>
        row.RecordKeeperTypes?.description ?? row.recordKeeperTypeId
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <RkoActionsCell
          onDeleted={() =>
            deleteOrg.mutate({
              recordKeepingOrganizationId:
                params.row.recordKeepingOrganizationId
            })
          }
          org={params.row}
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  const organizations = useGetAdminRecordKeepingOrganizations(
    query as GetAdminRecordKeepingOrganizationsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const onOpenCreate = () => setIsCreateOpen(true);

  const onCloseCreate = () => {
    setIsCreateOpen(false);
    createMethods.reset();
  };

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Record Keeping Organizations</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.recordKeepingOrganizationId}
        initialSort={[{ field: 'name', sort: 'asc' }]}
        loading={!query || organizations.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={organizations.data?.pagination?.total ?? 0}
        rows={organizations.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'recordKeepingOrganizations'
          },
          toolbar: {
            addLabel: 'Record Keeper',
            addTestId: 'createRecordKeepingOrganization',
            onAdd: onOpenCreate,
            onRefresh: organizations.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'recordKeepersFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Dialog
        data-testid='createRecordKeepingOrganizationDialog'
        fullWidth
        isOpen={isCreateOpen}
        maxWidth='md'
        onClose={onCloseCreate}>
        <Form
          methods={createMethods}
          noValidate
          onSubmit={createMethods.handleSubmit(onCreateSubmit)}>
          <DialogTitle variant='h3'>
            Create Record Keeping Organization
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack spacing={2}>
                <Typography variant='h4'>Organization Info</Typography>
                <Divider />
                <Stack
                  alignItems='center'
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={3}>
                  <ImageUploader
                    alt='Logo preview'
                    data-testid='createLogoUrl'
                    emptyPlaceholder='No logo uploaded'
                    invalidTypeMessage='Logo must be a PNG, JPEG, or WebP image.'
                    isUploading={isUploadingLogo}
                    label='Upload logo'
                    onChange={key =>
                      createMethods.setValue('logoUrl', key, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    onUpload={({ content, contentType }) =>
                      uploadAsset.mutateAsync({
                        data: {
                          content,
                          contentType: contentType as UploadAssetBodyContentType
                        },
                        kind: 'logos'
                      })
                    }
                    previewSrc={logoUrl ? assetUrl(logoUrl) : undefined}
                    previewVariant='image'
                    registerCtx={createMethods.register('logoUrl')}
                    value={logoUrl}
                  />
                  <Stack flex={1} spacing={2} width='100%'>
                    <Input
                      data-testid='createName'
                      format='string'
                      fullWidth
                      hideError
                      label='Name'
                      name='name'
                      required
                      size='small'
                    />
                    <SelectInput
                      fullWidth
                      hideError
                      label='Record Keeper Type'
                      name='recordKeeperTypeId'
                      options={recordKeeperTypeOptions}
                      placeholder='Select a type'
                      required
                      size='small'
                      variant='outlined'
                    />
                  </Stack>
                </Stack>
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='createRecordKeeperClientId'
                    format='string'
                    fullWidth
                    hideError
                    label='Record Keeper Client ID'
                    name='recordKeeperClientId'
                    size='small'
                  />
                  <SelectInput
                    fullWidth
                    hideError
                    label='Billing Frequency'
                    name='billingFrequency'
                    options={billingFrequencyOptions}
                    placeholder='Select frequency'
                    size='small'
                    variant='outlined'
                  />
                </Stack>
                <Input
                  data-testid='createRecordKeeperUrl'
                  format='string'
                  fullWidth
                  hideError
                  label='Record Keeper URL'
                  name='recordKeeperUrl'
                  size='small'
                />
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>Contact Information</Typography>
                <Divider />
                <Input
                  data-testid='createContact'
                  format='string'
                  fullWidth
                  hideError
                  label='Contact'
                  name='contact'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='createPhone'
                    format='string'
                    fullWidth
                    hideError
                    label='Phone'
                    name='phone'
                    size='small'
                  />
                  <Input
                    data-testid='createEmail'
                    format='string'
                    fullWidth
                    hideError
                    label='Email'
                    name='email'
                    size='small'
                  />
                </Stack>
                <Input
                  data-testid='createAddress'
                  format='string'
                  fullWidth
                  hideError
                  label='Address'
                  name='address'
                  size='small'
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
                  <Input
                    data-testid='createCity'
                    format='string'
                    fullWidth
                    hideError
                    label='City'
                    name='city'
                    size='small'
                  />
                  <Input
                    data-testid='createState'
                    format='string'
                    fullWidth
                    hideError
                    label='State'
                    name='state'
                    size='small'
                  />
                  <Input
                    data-testid='createZip'
                    format='string'
                    fullWidth
                    hideError
                    label='Zip'
                    name='zip'
                    size='small'
                  />
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant='h4'>TIAA Fields</Typography>
                <Divider />
                <Input
                  data-testid='createTiaaTargetSourceId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Target Source ID'
                  name='tiaaTargetSourceId'
                  size='small'
                />
                <Input
                  data-testid='createTiaaManagedSourceId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Managed Source ID'
                  name='tiaaManagedSourceId'
                  size='small'
                />
                <Input
                  data-testid='createTiaaTrustId'
                  format='string'
                  fullWidth
                  hideError
                  label='TIAA Trust ID'
                  name='tiaaTrustId'
                  size='small'
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelCreateRecordKeepingOrganization'
              onClick={onCloseCreate}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='submitCreateRecordKeepingOrganization'
              disabled={isUploadingLogo || createOrg.isPending}
              triggerValidation
              variant='contained'>
              Create
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>
    </Stack>
  );
};

export const Route = createFileRoute('/recordkeepers/')({
  component: RecordKeepingOrganizationsPage
});
